import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { CambiarEstadoPedidoAction } from "../actions";
import { useToast } from "vue-toastification";
import { invalidateDashboardQueries } from "@/utils/helper/invalidateDashboard";

const toast = useToast();

export function useCambiarEstadoPedido() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ pedidoId, nombreEstado }: { pedidoId: string; nombreEstado: string }) =>
      CambiarEstadoPedidoAction(pedidoId, nombreEstado),
    
    onMutate: async (variables) => {
      // Cancelar queries en curso para evitar sobrescrituras
      await queryClient.cancelQueries({ queryKey: ["gpedListPedidos"] });
      await queryClient.cancelQueries({ 
        queryKey: ["gepPedidoDetalles", variables.pedidoId] 
      });

      // Guardar snapshot anterior por si necesitamos revertir
      const previousPedidos = queryClient.getQueryData(["gpedListPedidos"]);
      const previousDetalles = queryClient.getQueryData([
        "gepPedidoDetalles", 
        variables.pedidoId
      ]);

      // Actualizar el caché de la lista optimísticamente
      queryClient.setQueryData(["gpedListPedidos"], (old: any) => {
        if (!old?.data?.data?.pedidos) return old;

        return {
          ...old,
          data: {
            ...old.data,
            data: {
              ...old.data.data,
              pedidos: old.data.data.pedidos.map((pedido: any) =>
                pedido.id === variables.pedidoId
                  ? {
                      ...pedido,
                      estadoPedido: {
                        ...pedido.estadoPedido,
                        nombre: variables.nombreEstado,
                      },
                    }
                  : pedido
              ),
            },
          },
        };
      });

      // Actualizar el caché de los detalles optimísticamente
      queryClient.setQueryData(
        ["gepPedidoDetalles", variables.pedidoId],
        (old: any) => {
          if (!old?.data?.data) return old;

          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                estadoPedido: {
                  ...old.data.data.estadoPedido,
                  nombre: variables.nombreEstado,
                },
              },
            },
          };
        }
      );

      // Retornar contexto para rollback
      return { previousPedidos, previousDetalles };
    },
    
    onError: (error: any, variables, context: any) => {
      // Revertir al estado anterior si hay error
      if (context?.previousPedidos) {
        queryClient.setQueryData(["gpedListPedidos"], context.previousPedidos);
      }
      if (context?.previousDetalles) {
        queryClient.setQueryData(
          ["gepPedidoDetalles", variables.pedidoId],
          context.previousDetalles
        );
      }
      toast.error("Error al cambiar el estado del pedido");
    },

    onSettled: async (data, error, variables) => {
      // Sincronizar con el servidor después de todo
      await queryClient.invalidateQueries({ queryKey: ["gpedListPedidos"] });
      await queryClient.invalidateQueries({ 
        queryKey: ["gepPedidoDetalles", variables.pedidoId] 
      });
      await invalidateDashboardQueries(queryClient);

      // Mostrar toast solo si no hubo error
      if (!error) {
        toast.success("Estado del pedido actualizado correctamente");
      }
    },
  });

  return {
    ...mutation,
  };
}